import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { IMAGES, INTERIOR_SECTORS } from "@/data/content";

const Interiors = () => (
    <div data-testid="interiors-page">
        <Seo
            title="Turnkey Interior Design & Execution | Kavraj Spaces — Delhi"
            description="Turnkey interior design and execution across India — residences in Delhi and Gurgaon, corporate offices, hospitality, franchises, hospitals, government departments and institutions."
        />

        <PageHero
            eyebrow="Capability 02 — Interiors"
            title="Turnkey interiors, delivered whole."
            intro="Design and execution under a single accountable team — across homes, workplaces, hospitality, healthcare, government and institutional settings, anywhere in India."
            testid="interiors-hero"
        />

        <section className="bg-cream" data-testid="interiors-intro">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-32">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        One team, end to end
                    </p>
                    <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        From drawings to handover, nothing in between is left to chance.
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-stone2">
                        A turnkey mandate means one brief, one team and one standard — design,
                        civil work, services, finishes, furniture and final styling coordinated as a
                        single programme. The client deals with one partner; we manage everything
                        behind that promise.
                    </p>
                    <p className="mt-5 text-base leading-relaxed text-stone2">
                        Because our furniture division works alongside the design team, joinery and
                        loose furniture are planned with the space — not fitted around it afterwards.
                    </p>
                </Reveal>
                <Reveal delay={120}>
                    <img
                        src={IMAGES.interiors.url}
                        alt={IMAGES.interiors.alt}
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                </Reveal>
            </div>
        </section>

        <section className="border-y border-bronze/15 bg-cream-muted" data-testid="interiors-sectors">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        Sectors we serve
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Every kind of space, held to the same standard.
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {INTERIOR_SECTORS.map((sector, i) => (
                        <Reveal key={sector.title} delay={(i % 3) * 100}>
                            <div
                                className="h-full border border-bronze/15 bg-cream p-8 transition-colors duration-300 hover:border-bronze/40"
                                data-testid={`sector-card-${i}`}
                            >
                                <h3 className="font-serif text-xl text-ink">{sector.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-stone2">{sector.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={200}>
                    <p className="mt-12 max-w-2xl text-sm leading-relaxed text-stone2">
                        Residential work is centred in Delhi and Gurgaon; franchise, institutional
                        and project work extends across India — with interiors delivered at more than
                        a hundred franchise locations.
                    </p>
                </Reveal>
            </div>
        </section>

        <CtaBand
            title="Planning a space, or a hundred of them?"
            text="From a single residence to a multi-city rollout, the conversation starts the same way."
        />
    </div>
);

export default Interiors;
